import { useMemo, useState } from 'react';
import { formatMoney } from '@/utils/format';
import { loadBillers, persistBillers, toggleBillerFavourite } from '@/utils/billerStore';
import type { Biller, FundingWallet, SinglePayment } from '@/types/portal';
import type { PaymentSentMeta } from '../types';
import BillFormStep from './BillFormStep';
import BillConfirmStep from './BillConfirmStep';
import BillFavouritesModal from './BillFavouritesModal';
import { imageUrl } from '@/utils/assets';

type BillView = 'form' | 'confirm';

interface PayBillSheetProps {
  wallets: FundingWallet[];
  canManage: boolean;
  canApprove: boolean;
  counter: number;
  onPaymentSent: (payment: SinglePayment, meta: PaymentSentMeta) => void;
  onClose: () => void;
  /** A paybill to prefill, e.g. from a deep link. */
  initialPaybill?: string | null;
}

/** Excise/interest charged on a paybill settlement, as a share of the amount. */
const INTEREST_RATE = 0.01;

const BILL_AVATAR = imageUrl('payments/pay-bill-biller.webp');

/**
 * Dedicated "Pay a Bill" sheet. Captures a paybill, account number and amount,
 * then shows a "Pay to Paybill" confirmation before submitting. The host page
 * owns open/close state; this component owns the two-screen stack.
 */
export default function PayBillSheet({
  wallets,
  canManage,
  canApprove,
  counter,
  onPaymentSent,
  onClose,
  initialPaybill = null,
}: PayBillSheetProps) {
  const [view, setView] = useState<BillView>('form');
  const [billers, setBillers] = useState<Biller[]>(() => loadBillers());
  const [manageOpen, setManageOpen] = useState(false);
  const [selectedBillerId, setSelectedBillerId] = useState<string | null>(null);
  const [paybill, setPaybill] = useState(initialPaybill ?? '');
  const [account, setAccount] = useState('');
  const [amount, setAmount] = useState(0);
  const [walletId, setWalletId] = useState(
    () => wallets.find((wallet) => wallet.currency === 'KES')?.id ?? wallets[0]?.id ?? '',
  );

  const favourites = useMemo(
    () => billers.filter((biller) => biller.favourite),
    [billers],
  );
  const selectedBiller = billers.find((biller) => biller.id === selectedBillerId) ?? null;
  const wallet = wallets.find((item) => item.id === walletId) ?? null;
  const money = wallet?.currency ?? 'KES';

  const transactionCost = 0;
  const interestFee = amount > 0 ? Math.round(amount * INTEREST_RATE * 100) / 100 : 0;
  const total = amount + interestFee;
  const shortfall = wallet ? Math.max(0, total - wallet.balance) : 0;
  const covered = wallet !== null && shortfall <= 0;

  const paybillValid = /^\d{5,7}$/.test(paybill);
  const accountValid = account.trim().length >= 4;
  const amountValid = amount > 0;

  const error = useMemo(() => {
    if (!canManage) return '';
    if (!wallet) return 'Select a wallet to pay from.';
    if (!paybillValid) return 'Enter a valid paybill number (5–7 digits).';
    if (!accountValid) return 'Enter the account number for this biller.';
    if (!amountValid) return 'Enter the amount you want to pay.';
    if (!covered) return `Funding wallet is short by ${formatMoney(shortfall, money)}.`;
    return '';
  }, [canManage, wallet, paybillValid, accountValid, amountValid, covered, shortfall, money]);

  const canContinue = canManage && error === '';

  const handleSelectBiller = (biller: Biller) => {
    setSelectedBillerId(biller.id);
    setPaybill(biller.paybill);
  };

  const handleToggleFavourite = (id: string) => {
    setBillers((prev) => {
      const next = toggleBillerFavourite(prev, id);
      persistBillers(next);
      return next;
    });
  };

  const handlePay = () => {
    if (!wallet || !canContinue) return;
    const status = canApprove ? 'Paid' : 'Awaiting approval';
    const payment: SinglePayment = {
      id: `sp-${counter}`,
      reference: `PAY-${counter}`,
      payeeName: selectedBiller?.name ?? `Paybill ${paybill}`,
      avatar: BILL_AVATAR,
      channel: 'bill',
      target: `${paybill} · ${account}`,
      amount,
      currency: money,
      rail: `Paybill · ${money}`,
      status,
      createdAt: '16 Sep 2026',
      createdBy: 'Peter Kironji',
    };
    onPaymentSent(payment, { newPayee: null, request: { sourceWalletId: wallet.id, destination: { paybill, account } } });
  };

  return (
    <div>
      {/* Sheet header */}
      <div className="mb-6 flex items-center gap-3 border-b border-background-200 pb-4">
        {view === 'confirm' ? (
          <button
            type="button"
            onClick={() => setView('form')}
            aria-label="Back"
            className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full text-foreground-700 transition-colors hover:bg-background-200"
          >
            <i className="ri-arrow-left-s-line text-lg" />
          </button>
        ) : null}
        <h2 className="flex-1 font-heading text-base font-semibold text-foreground-950">
          {view === 'confirm' ? 'Confirm' : 'Pay a Bill'}
        </h2>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full text-foreground-500 transition-colors hover:bg-background-200 hover:text-foreground-900"
        >
          <i className="ri-close-line text-lg" />
        </button>
      </div>

      {view === 'form' ? (
        <BillFormStep
          favourites={favourites}
          selectedBillerId={selectedBillerId}
          onSelectBiller={handleSelectBiller}
          onManageFavourites={() => setManageOpen(true)}
          paybill={paybill}
          onPaybill={setPaybill}
          account={account}
          onAccount={setAccount}
          amount={amount}
          onAmount={setAmount}
          currency={money}
          wallets={wallets}
          walletId={walletId}
          onWallet={setWalletId}
          fee={transactionCost}
          total={total}
          error={error}
          canManage={canManage}
          canContinue={canContinue}
          onContinue={() => setView('confirm')}
        />
      ) : (
        <BillConfirmStep
          biller={selectedBiller}
          paybill={paybill}
          account={account}
          amount={amount}
          currency={money}
          transactionCost={transactionCost}
          interestFee={interestFee}
          total={total}
          wallet={wallet}
          canApprove={canApprove}
          onPay={handlePay}
        />
      )}

      <BillFavouritesModal
        open={manageOpen}
        onClose={() => setManageOpen(false)}
        billers={billers}
        onToggle={handleToggleFavourite}
      />
    </div>
  );
}